import React, { useMemo, memo } from 'react';
import { Card } from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { OrderStatusBreakdown } from '../../services/dashboardService';
import { DashboardCardHeader } from './DashboardCardHeader';
import { ChartSkeleton } from './ChartSkeleton';
import { DashboardEmptyState } from './DashboardEmptyState';

interface OrderStatusDistributionChartProps {
  data: OrderStatusBreakdown;
  rangeLabel?: string;
  loading?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#faad14',
  confirmed: '#1890ff',
  preparing: '#722ed1',
  ready: '#13c2c2',
  delivering: '#2f54eb',
  delivered: '#52c41a',
  cancelled: '#ff4d4f',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Chờ xác nhận',
  confirmed: 'Đã xác nhận',
  preparing: 'Đang chuẩn bị',
  ready: 'Sẵn sàng',
  delivering: 'Đang giao',
  delivered: 'Đã giao',
  cancelled: 'Đã hủy',
};

const OrderStatusDistributionChart: React.FC<
  OrderStatusDistributionChartProps
> = ({ data, rangeLabel, loading = false }) => {
  const chartData = useMemo(() => {
    if (!data) return [];
    const d = data as unknown as Record<string, number>;
    const total = Object.keys(data).reduce((s, k) => {
      const v = d[k];
      return s + (typeof v === 'number' ? v : 0);
    }, 0);
    if (total === 0) return [];
    return Object.entries(data)
      .filter(([k]) => k !== 'date' && typeof d[k] === 'number')
      .map(([key]) => ({
        key,
        name: STATUS_LABELS[key] || key,
        value: d[key] || 0,
        percent: total > 0 ? Math.round((d[key] / total) * 100) : 0,
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data]);

  if (loading) {
    return (
      <Card className="admin-order-status-card">
        <DashboardCardHeader
          title="Trạng thái đơn hàng"
          subtitle={rangeLabel}
          infoTooltip="Phân bổ số đơn theo từng trạng thái trong khoảng thời gian đã chọn."
        />
        <ChartSkeleton height={280} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card className="admin-order-status-card">
        <DashboardCardHeader
          title="Trạng thái đơn hàng"
          subtitle={rangeLabel}
          infoTooltip="Phân bổ số đơn theo từng trạng thái trong khoảng thời gian đã chọn."
        />
        <DashboardEmptyState
          message="Chưa có đơn hàng trong khoảng thời gian đã chọn."
          suggestion="Thử mở rộng phạm vi ngày."
        />
      </Card>
    );
  }

  const total = chartData.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="admin-order-status-card">
      <DashboardCardHeader
        title="Trạng thái đơn hàng"
        subtitle={rangeLabel}
        infoTooltip="Phân bổ số đơn theo từng trạng thái trong khoảng thời gian đã chọn."
      />
      <div style={{ padding: '8px 0' }}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              label={({ name, percent }) => `${name}: ${percent}%`}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.key}
                  fill={
                    STATUS_COLORS[entry.key] || `hsl(${index * 45}, 70%, 55%)`
                  }
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string, props: unknown) => {
                const p = props as { payload?: { percent?: number } };
                const pct = p?.payload?.percent ?? 0;
                return [`${value} đơn (${pct}%)`, name];
              }}
              contentStyle={{
                backgroundColor: 'rgba(255,255,255,0.98)',
                border: '1px solid #e8e8e8',
                borderRadius: 4,
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            textAlign: 'center',
            marginTop: 8,
            fontSize: 13,
            color: '#666',
          }}
        >
          Tổng: <strong>{total.toLocaleString('vi-VN')}</strong> đơn
        </div>
      </div>
    </Card>
  );
};

export default memo(OrderStatusDistributionChart);
